import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TestimonialSubmissionForm from '../components/TestimonialSubmissionForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitForm, setShowSubmitForm] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/testimonials`);
      setTestimonials(response.data.testimonials || []);
    } catch (err) {
      console.error('Failed to fetch testimonials:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <span key={index} className={index < rating ? 'text-yellow-400' : 'text-gray-300'}>
        ★
      </span>
    ));
  };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center">
            Customer Testimonials
          </h1>
          <p className="text-xl text-center text-blue-100 mb-8">
            See what our customers have to say about our services
          </p>
          <div className="text-center">
            <button
              onClick={() => setShowSubmitForm(true)}
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition inline-flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Share Your Experience
            </button>
          </div>
        </div>
      </div>

      {/* Testimonials Grid */}
      <div className="container mx-auto px-4 py-12">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div key={n} className="bg-white rounded-lg shadow p-6 animate-pulse">
                <div className="h-4 bg-gray-300 rounded mb-4"></div>
                <div className="h-20 bg-gray-300 rounded mb-4"></div>
                <div className="h-4 bg-gray-300 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : testimonials.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No testimonials yet</h3>
            <p className="text-gray-500 mb-4">Be the first to share your experience!</p>
            <button
              onClick={() => setShowSubmitForm(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Share Your Experience
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow p-6 flex flex-col"
              >
                {/* Stars */}
                <div className="flex text-2xl mb-4">
                  {renderStars(testimonial.rating)}
                </div>

                {/* Testimonial Text */}
                <p className="text-gray-700 mb-6 flex-grow leading-relaxed">
                  "{testimonial.testimonial_text}"
                </p>

                {/* Customer Info */}
                <div className="border-t pt-4">
                  <h4 className="font-bold text-gray-900">{testimonial.customer_name}</h4>
                  {testimonial.job_title && (
                    <p className="text-sm text-blue-600 font-medium">{testimonial.job_title}</p>
                  )}
                  <p className="text-sm text-gray-600">{testimonial.company_name}</p>
                  <p className="text-xs text-gray-500 mt-2">{testimonial.service_type}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatDate(testimonial.submitted_at)}</p>
                </div>

                {/* Featured Badge */}
                {testimonial.is_featured && (
                  <div className="absolute top-4 right-4 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold">
                    ⭐ Featured
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Submission Form Modal */}
      {showSubmitForm && (
        <TestimonialSubmissionForm
          onClose={() => setShowSubmitForm(false)}
          onSuccess={() => {
            setShowSubmitForm(false);
            alert('Thank you for your testimonial! It will be reviewed and published soon.');
          }}
        />
      )}
    </div>
  );
};

export default Testimonials;
